import { useState, useCallback } from 'react'
import { api } from '../api/client.js'

export interface PasswordPolicyResponse {
  id: string
  tenantId: string
  status: boolean
  mfaPolicy?: 'admins_only' | 'all_users' | 'optional'
  minimumPasswordLength: number
  passwordExpirationPeriod?: number
  numberOfPasswordsToRemember?: number
  invalidPasswordAttempts?: number
  accountLockoutDuration?: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumber: boolean
  requireNonAlphanumeric: boolean
  allowUsersToChangeOwnPassword: boolean
  enablePasswordExpiration: boolean
  preventPasswordReuse: boolean
  passwordExpirationRequiresAdminReset: boolean
  createdAt: string
  updatedAt: string
}

export interface PasswordPolicyCreateInput {
  status?: boolean
  mfaPolicy?: 'admins_only' | 'all_users' | 'optional'
  minimumPasswordLength?: number
  passwordExpirationPeriod?: number | undefined
  numberOfPasswordsToRemember?: number | undefined
  invalidPasswordAttempts?: number | undefined
  accountLockoutDuration?: number | undefined
  requireUppercase?: boolean
  requireLowercase?: boolean
  requireNumber?: boolean
  requireNonAlphanumeric?: boolean
  allowUsersToChangeOwnPassword?: boolean
  enablePasswordExpiration?: boolean
  preventPasswordReuse?: boolean
  passwordExpirationRequiresAdminReset?: boolean
}

export interface PasswordPolicyUpdateInput {
  status?: boolean
  mfaPolicy?: 'admins_only' | 'all_users' | 'optional'
  minimumPasswordLength?: number
  passwordExpirationPeriod?: number | undefined
  numberOfPasswordsToRemember?: number | undefined
  invalidPasswordAttempts?: number | undefined
  accountLockoutDuration?: number | undefined
  requireUppercase?: boolean
  requireLowercase?: boolean
  requireNumber?: boolean
  requireNonAlphanumeric?: boolean
  allowUsersToChangeOwnPassword?: boolean
  enablePasswordExpiration?: boolean
  preventPasswordReuse?: boolean
  passwordExpirationRequiresAdminReset?: boolean
}

export function usePasswordPolicies() {
  const [items, setItems] = useState<PasswordPolicyResponse[]>([])
  const [loading, setLoading] = useState(false)

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get<{ passwordPolicies: PasswordPolicyResponse[] }>('/admin/password-policies')
      setItems(response.passwordPolicies)
    } catch (error) {
      console.error('Error fetching password policies:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  const getById = useCallback(async (id: string): Promise<PasswordPolicyResponse> => {
    setLoading(true)
    try {
      const response = await api.get<{ passwordPolicy: PasswordPolicyResponse }>(`/admin/password-policies/${id}`)
      return response.passwordPolicy
    } catch (error) {
      console.error('Error fetching password policy:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const create = useCallback(async (data: PasswordPolicyCreateInput): Promise<PasswordPolicyResponse> => {
    setLoading(true)
    try {
      const response = await api.post<{ passwordPolicy: PasswordPolicyResponse }>('/admin/password-policies', data)
      return response.passwordPolicy
    } catch (error) {
      console.error('Error creating password policy:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const update = useCallback(async (id: string, data: PasswordPolicyUpdateInput): Promise<PasswordPolicyResponse> => {
    setLoading(true)
    try {
      const response = await api.put<{ passwordPolicy: PasswordPolicyResponse }>(`/admin/password-policies/${id}`, data)
      return response.passwordPolicy
    } catch (error) {
      console.error('Error updating password policy:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteItem = useCallback(async (id: string): Promise<void> => {
    setLoading(true)
    try {
      await api.delete(`/admin/password-policies/${id}`)
    } catch (error) {
      console.error('Error deleting password policy:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    items,
    loading,
    fetchItems,
    getById,
    create,
    update,
    deleteItem,
  }
}
